import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCanAccess, useDataProvider, useNotify, useShowContext } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';

import type { CrmDataProvider } from '@/providers/types';
import { QuoteJobFolder } from '@/types';
import { SafeHtml } from '@/components/misc/SafeHtml';
import { QuoteDriveExplorer } from '@/modules/quotes/QuoteDriveExplorer';
import { ProjectPermitsDialog } from './ProjectPermitsDialog';
import type { PermitPackage } from './projectPermitsTypes';

export const ProjectPermitsTab = () => {
    const { record } = useShowContext<QuoteJobFolder>();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const queryClient = useQueryClient();
    const { canAccess: canEditPermits = false } = useCanAccess({
        resource: 'permit',
        action: 'update',
    });

    const [loading, setLoading] = React.useState(true);
    const [packageData, setPackageData] = React.useState<PermitPackage | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editingDescription, setEditingDescription] = React.useState(false);
    const [descriptionDraft, setDescriptionDraft] = React.useState('');
    const [savingDescription, setSavingDescription] = React.useState(false);
    const [editingMunicipality, setEditingMunicipality] = React.useState(false);
    const [municipalityDraft, setMunicipalityDraft] = React.useState('');
    const [savingMunicipality, setSavingMunicipality] = React.useState(false);

    const loadPackage = React.useCallback(async () => {
        if (!record?.id) return;
        return dataProvider.getProjectPermits(record.id);
    }, [record?.id, dataProvider]);

    React.useEffect(() => {
        if (!record?.id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        loadPackage()
            .then(data => {
                if (cancelled || !data) return;
                setPackageData(data);
            })
            .catch(err => {
                if (!cancelled) setError(err?.message || 'Could not load permit documents.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [record?.id, loadPackage]);

    const refetch = async () => {
        try {
            const next = await loadPackage();
            if (next) setPackageData(next);
        } catch (err: any) {
            notify(err?.message || 'Could not reload permit documents.', { type: 'error' });
        }
    };

    const handleFilesGenerated = React.useCallback(() => {
        if (packageData?.primary_quote_id) {
            queryClient.invalidateQueries({ queryKey: ['quoteDrive', packageData.primary_quote_id] });
        }
    }, [queryClient, packageData?.primary_quote_id]);

    const startEditMunicipality = () => {
        setMunicipalityDraft(packageData?.municipality_key ?? '');
        setEditingMunicipality(true);
    };

    const saveMunicipality = async () => {
        if (!municipalityDraft) return;
        setSavingMunicipality(true);
        try {
            await dataProvider.setProjectPermitMunicipality(
                record!.id,
                municipalityDraft,
            );
            // The save response has no sections; refetch so the include is sent.
            await refetch();
            setEditingMunicipality(false);
            notify('Permit municipality saved', { type: 'success' });
        } catch (err: any) {
            notify(err?.message || 'Could not save municipality.', { type: 'error' });
        } finally {
            setSavingMunicipality(false);
        }
    };

    const startEditDescription = () => {
        setDescriptionDraft(packageData?.apply_description ?? '');
        setEditingDescription(true);
    };

    const saveDescription = async () => {
        if (!packageData?.municipality_id) {
            notify('No municipality selected for this project yet.', { type: 'warning' });
            return;
        }
        setSavingDescription(true);
        try {
            await dataProvider.update('municipalities', {
                id: packageData.municipality_id,
                data: { apply_description: descriptionDraft },
                previousData: { id: packageData.municipality_id },
            });
            notify('Municipality updated', { type: 'success' });
            setEditingDescription(false);
            await refetch();
        } catch (err: any) {
            notify(err?.message || 'Could not update municipality.', { type: 'error' });
        } finally {
            setSavingDescription(false);
        }
    };

    if (loading) {
        return (
            <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
            </Stack>
        );
    }
    if (error) {
        return (
            <Card sx={{ p: 2 }}>
                <Typography color="text.secondary">{error}</Typography>
            </Card>
        );
    }
    if (!packageData) return null;

    return (
        <Stack gap={2}>
            <Card sx={{ p: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    gap={1}
                >
                    <Box sx={{ flex: 1 }}>
                        {editingMunicipality ? (
                            <Stack direction="row" gap={1} alignItems="center">
                                <FormControl size="small" sx={{ minWidth: 240 }}>
                                    <InputLabel id="permit-tab-municipality-label">
                                        Municipality
                                    </InputLabel>
                                    <Select
                                        labelId="permit-tab-municipality-label"
                                        label="Municipality"
                                        value={municipalityDraft}
                                        onChange={e =>
                                            setMunicipalityDraft(String(e.target.value))
                                        }
                                    >
                                        {(packageData.available_municipalities ?? []).map(
                                            option => (
                                                <MenuItem key={option.key} value={option.key}>
                                                    {option.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={saveMunicipality}
                                    disabled={!municipalityDraft || savingMunicipality}
                                >
                                    {savingMunicipality ? 'Saving…' : 'Save'}
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => setEditingMunicipality(false)}
                                    disabled={savingMunicipality}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="h6">
                                    {packageData.municipality_label}
                                </Typography>
                                {canEditPermits ? (
                                    <Button
                                        size="small"
                                        startIcon={<EditOutlinedIcon fontSize="small" />}
                                        onClick={startEditMunicipality}
                                        disabled={
                                            !(packageData.available_municipalities ?? []).length
                                        }
                                    >
                                        Change
                                    </Button>
                                ) : null}
                            </Stack>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Folio {packageData.folio_number || '—'}
                            {packageData.folio_municipality_code
                                ? ` · prefix ${packageData.folio_municipality_code}`
                                : ''}
                        </Typography>
                        {packageData.status_message && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {packageData.status_message}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" gap={1}>
                        <Button variant="contained" onClick={() => setDialogOpen(true)}>
                            Open permit documents
                        </Button>
                    </Stack>
                </Stack>

                <Box sx={{ mt: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                    >
                        <Typography variant="subtitle2" color="text.secondary">
                            How to apply
                        </Typography>
                        {canEditPermits && !editingDescription ? (
                            <Button
                                size="small"
                                startIcon={<EditOutlinedIcon fontSize="small" />}
                                onClick={startEditDescription}
                                disabled={!packageData.municipality_id}
                            >
                                Edit
                            </Button>
                        ) : null}
                    </Stack>
                    {editingDescription ? (
                        <Stack gap={1}>
                            <TextField
                                multiline
                                minRows={6}
                                value={descriptionDraft}
                                onChange={e => setDescriptionDraft(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <Stack direction="row" gap={1} justifyContent="flex-end">
                                <Button
                                    size="small"
                                    onClick={() => setEditingDescription(false)}
                                    disabled={savingDescription}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={saveDescription}
                                    disabled={savingDescription}
                                >
                                    {savingDescription ? 'Saving…' : 'Save'}
                                </Button>
                            </Stack>
                        </Stack>
                    ) : packageData.apply_description ? (
                        <SafeHtml html={packageData.apply_description} />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No instructions on file for this municipality.
                        </Typography>
                    )}
                </Box>
            </Card>

            {packageData.primary_quote_id ? (
                <Card sx={{ p: 2 }}>
                    <QuoteDriveExplorer
                        quoteId={packageData.primary_quote_id}
                        defaultRootPath={packageData.active_permits_folder_path ?? undefined}
                        rootLabel="Files in Permits folder"
                        readOnly={false}
                    />
                </Card>
            ) : (
                <Card sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        No primary quote for permits yet.
                    </Typography>
                </Card>
            )}

            {record?.id ? (
                <ProjectPermitsDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    projectId={record.id}
                    initialPackage={packageData}
                    onPackageChange={setPackageData}
                    onFilesGenerated={handleFilesGenerated}
                />
            ) : null}
        </Stack>
    );
};
